import React, { useRef, useState } from "react";
import { CMS_SECTIONS } from "../../lib/cmsSchema";
import { useCms } from "../../context/CmsContext";
import { Save, RotateCcw, ChevronDown, ChevronRight, ExternalLink, Upload, Image as ImageIcon } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Link } from "react-router-dom";

const SECTION_LINKS = {
    branding: "/",
    theme: "/",
    accueil: "/",
    apropos: "/a-propos",
    accompagnements: "/accompagnements",
    contact: "/contact",
    footer: "/",
};

export default function Contenus() {
    const cms = useCms();
    const [open, setOpen] = useState({ accueil: true });
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState({});

    const setDraft = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));

    const valueOf = (field) => {
        if (drafts[field.key] !== undefined) return drafts[field.key];
        return cms.get(field.key, field.default);
    };

    const isDirty = (field) =>
        drafts[field.key] !== undefined && drafts[field.key] !== cms.get(field.key, field.default);

    const saveField = async (field) => {
        if (!isDirty(field)) return;
        setSaving((s) => ({ ...s, [field.key]: true }));
        try {
            await cms.update(field.key, drafts[field.key]);
            setDrafts((d) => {
                const nd = { ...d };
                delete nd[field.key];
                return nd;
            });
            toast.success("Texte enregistré");
        } catch (e) {
            toast.error("Erreur — réessayez");
        } finally {
            setSaving((s) => ({ ...s, [field.key]: false }));
        }
    };

    const resetField = async (field) => {
        if (!window.confirm("Restaurer le texte par défaut ?")) return;
        try {
            await cms.update(field.key, field.default);
            setDrafts((d) => {
                const nd = { ...d };
                delete nd[field.key];
                return nd;
            });
            toast.success("Restauré");
        } catch {
            toast.error("Erreur");
        }
    };

    const saveSection = async (section) => {
        const dirtyFields = section.fields.filter(isDirty);
        if (dirtyFields.length === 0) {
            toast.message("Aucun changement à enregistrer");
            return;
        }
        for (const f of dirtyFields) {
            await saveField(f);
        }
    };

    if (!cms.loaded) {
        return <div className="text-white/60 text-sm">Chargement…</div>;
    }

    return (
        <div data-testid="dashboard-contenus" className="space-y-8 max-w-4xl">
            <Toaster position="top-center" theme="dark" richColors />
            <div>
                <span className="text-xs tracking-[0.3em] text-ice uppercase">— Édition</span>
                <h1 className="font-serif text-4xl mt-2">Contenus du site</h1>
                <p className="text-white/65 mt-3 max-w-2xl">
                    Modifie ici tous les textes du site public. Les changements sont visibles immédiatement après
                    enregistrement.
                </p>
            </div>

            <div className="space-y-4">
                {CMS_SECTIONS.map((section) => {
                    const isOpen = !!open[section.id];
                    const dirtyCount = section.fields.filter(isDirty).length;
                    return (
                        <section
                            key={section.id}
                            data-testid={`cms-section-${section.id}`}
                            className="bg-ink-600 border border-white/10 rounded-xl overflow-hidden"
                        >
                            <button
                                onClick={() => setOpen((o) => ({ ...o, [section.id]: !isOpen }))}
                                className="w-full flex items-center justify-between gap-4 p-5 hover:bg-ink-700 transition-colors text-left"
                            >
                                <div className="flex items-center gap-3">
                                    {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                    <span className="font-serif text-xl">{section.label}</span>
                                    {dirtyCount > 0 && (
                                        <span className="text-xs bg-ice/20 text-ice border border-ice/40 px-2 py-0.5 rounded-full">
                                            {dirtyCount} modif.
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    {SECTION_LINKS[section.id] && (
                                        <Link
                                            to={SECTION_LINKS[section.id]}
                                            target="_blank"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-xs text-white/60 hover:text-ice flex items-center gap-1"
                                        >
                                            Voir la page <ExternalLink size={12} />
                                        </Link>
                                    )}
                                </div>
                            </button>
                            {isOpen && (
                                <div className="border-t border-white/10 p-6 space-y-5">
                                    {section.fields.map((field) => {
                                        if (field.type === "image") return <ImageField key={field.key} field={field} />;
                                        if (field.type === "color") return <ColorField key={field.key} field={field} />;
                                        const dirty = isDirty(field);
                                        const value = valueOf(field);
                                        const Input = field.multiline ? "textarea" : "input";
                                        return (
                                            <div key={field.key}>
                                                <div className="flex items-center justify-between gap-3 mb-2">
                                                    <label className="text-xs tracking-[0.2em] text-white/60 uppercase">
                                                        {field.label}
                                                    </label>
                                                    <div className="flex items-center gap-2">
                                                        {dirty && (
                                                            <span className="text-[10px] text-ice tracking-wide">
                                                                MODIFIÉ
                                                            </span>
                                                        )}
                                                        <button
                                                            onClick={() => resetField(field)}
                                                            className="text-[11px] text-white/40 hover:text-ice flex items-center gap-1"
                                                            title="Restaurer le texte par défaut"
                                                        >
                                                            <RotateCcw size={11} /> par défaut
                                                        </button>
                                                    </div>
                                                </div>
                                                <Input
                                                    value={value}
                                                    onChange={(e) => setDraft(field.key, e.target.value)}
                                                    rows={field.multiline ? Math.max(3, Math.ceil(value.length / 90)) : undefined}
                                                    data-testid={`cms-field-${field.key}`}
                                                    className={`w-full bg-ink-800 border ${
                                                        dirty ? "border-ice" : "border-white/10"
                                                    } text-white focus:border-ice focus:ring-1 focus:ring-ice rounded-md px-4 py-3 outline-none text-sm transition-colors ${
                                                        field.multiline ? "resize-y leading-relaxed" : ""
                                                    }`}
                                                />
                                                {dirty && (
                                                    <div className="flex justify-end mt-2">
                                                        <button
                                                            onClick={() => saveField(field)}
                                                            disabled={saving[field.key]}
                                                            data-testid={`cms-save-${field.key}`}
                                                            className="text-xs inline-flex items-center gap-1.5 bg-ice text-white hover:bg-ice-soft hover:text-ink-900 disabled:opacity-50 px-4 py-1.5 rounded-md font-medium transition-colors"
                                                        >
                                                            <Save size={12} />
                                                            {saving[field.key] ? "Enregistrement…" : "Enregistrer"}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {dirtyCount > 1 && (
                                        <div className="flex justify-end pt-2 border-t border-white/5">
                                            <button
                                                onClick={() => saveSection(section)}
                                                className="text-sm bg-ice text-white hover:bg-ice-soft hover:text-ink-900 px-5 py-2 rounded-md font-medium transition-colors inline-flex items-center gap-2"
                                            >
                                                <Save size={14} /> Tout enregistrer ({dirtyCount})
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>
        </div>
    );
}

function ImageField({ field }) {
    const cms = useCms();
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const value = cms.get(field.key, field.default);

    const onFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Sélectionnez une image");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Image trop volumineuse (max 10 Mo)");
            return;
        }
        setUploading(true);
        try {
            await cms.uploadImage(field.key, file);
            toast.success("Image mise à jour");
        } catch (e) {
            toast.error("Erreur d'envoi — réessayez");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const reset = async () => {
        if (!window.confirm("Restaurer l'image par défaut ?")) return;
        try {
            await cms.update(field.key, field.default);
            toast.success("Restauré");
        } catch {
            toast.error("Erreur");
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between gap-3 mb-2">
                <label className="text-xs tracking-[0.2em] text-white/60 uppercase">{field.label}</label>
                <button
                    onClick={reset}
                    className="text-[11px] text-white/40 hover:text-ice flex items-center gap-1"
                    title="Restaurer l'image par défaut"
                >
                    <RotateCcw size={11} /> par défaut
                </button>
            </div>
            <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
                className="bg-ink-800 border border-white/10 hover:border-ice/40 rounded-md p-4 flex items-center gap-5 transition-colors"
            >
                <div className="w-24 h-24 rounded-md bg-ink-900 border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
                    {value ? (
                        <img src={value} alt={field.label} className="w-full h-full object-contain" />
                    ) : (
                        <ImageIcon size={24} className="text-white/30" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs text-white/55 truncate">{value || "Aucune image"}</div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                        <button
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            data-testid={`cms-upload-${field.key}`}
                            className="text-xs inline-flex items-center gap-1.5 bg-ice text-white hover:bg-ice-soft hover:text-ink-900 disabled:opacity-50 px-4 py-1.5 rounded-md font-medium transition-colors"
                        >
                            <Upload size={12} />
                            {uploading ? "Envoi…" : "Remplacer l'image"}
                        </button>
                        <span className="text-[11px] text-white/40 self-center">JPG/PNG, 10 Mo max — glisser-déposer aussi possible</span>
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onFile(e.target.files?.[0])}
                    />
                </div>
            </div>
        </div>
    );
}

function ColorField({ field }) {
    const cms = useCms();
    const current = cms.get(field.key, field.default);
    const [val, setVal] = useState(current);
    const [saving, setSaving] = useState(false);
    const dirty = val.toLowerCase() !== (current || "").toLowerCase();

    const save = async () => {
        if (!dirty) return;
        setSaving(true);
        try {
            await cms.update(field.key, val);
            toast.success("Couleur enregistrée");
        } catch {
            toast.error("Erreur");
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!window.confirm("Restaurer la couleur par défaut ?")) return;
        try {
            await cms.update(field.key, field.default);
            setVal(field.default);
            toast.success("Restauré");
        } catch {
            toast.error("Erreur");
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between gap-3 mb-2">
                <label className="text-xs tracking-[0.2em] text-white/60 uppercase">{field.label}</label>
                <button onClick={reset} className="text-[11px] text-white/40 hover:text-ice flex items-center gap-1">
                    <RotateCcw size={11} /> par défaut
                </button>
            </div>
            <div className="bg-ink-800 border border-white/10 rounded-md p-4 flex items-center gap-4">
                <input
                    type="color"
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    data-testid={`cms-color-${field.key}`}
                    className="w-14 h-14 rounded-md border border-white/10 cursor-pointer bg-transparent"
                />
                <input
                    type="text"
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    pattern="^#[0-9a-fA-F]{6}$"
                    className="bg-ink-900 border border-white/10 text-white focus:border-ice rounded-md px-3 py-2 text-sm font-mono w-32 outline-none uppercase"
                />
                {dirty && (
                    <button
                        onClick={save}
                        disabled={saving}
                        className="text-xs inline-flex items-center gap-1.5 bg-ice text-white hover:bg-ice-soft hover:text-ink-900 disabled:opacity-50 px-4 py-2 rounded-md font-medium transition-colors"
                    >
                        <Save size={12} />
                        {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                )}
            </div>
        </div>
    );
}

